import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as TransferIcon,
  ListAlt as EntryIcon,
  Business as DeptIcon,
  Store as SupplierIcon,
  Receipt as GSTIcon,
  AccountBalance as PaymentIcon,
  PictureAsPdf as PdfIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const ReportsPage = () => {
  const { user } = useAuth();
  const isSupervisor = user?.role === 'supervisor';
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Existing data states
  const [siteSummary, setSiteSummary] = useState(null);
  const [materialUsage, setMaterialUsage] = useState(null);
  const [budgetReport, setBudgetReport] = useState(null);
  const [transferReport, setTransferReport] = useState(null);

  // New Phase 1 data states
  const [entrySummary, setEntrySummary] = useState(null);
  const [departmentSpending, setDepartmentSpending] = useState(null);
  const [supplierSpending, setSupplierSpending] = useState(null);
  const [gstSummary, setGSTSummary] = useState(null);
  const [paymentSource, setPaymentSource] = useState(null);

  const [sites, setSites] = useState([]);

  // Common filters
  const [filters, setFilters] = useState({ startDate: '', endDate: '', siteId: '' });

  // Transfer report filters
  const [transferFilters, setTransferFilters] = useState({
    startDate: '', endDate: '', status: 'all', fromSiteId: '', toSiteId: ''
  });

  useEffect(() => { fetchSites(); }, []);

 useEffect(() => {
    if (!isSupervisor) fetchReportData();
    if (isSupervisor) fetchSupervisorData();
  }, [activeTab]);

  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data.data.sites || []);
    } catch (err) {
      console.error('Error fetching sites:', err);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.siteId) params.append('siteId', filters.siteId);
      const queryString = params.toString();

      switch (activeTab) {
        case 0:
          const siteRes = await axios.get(`${API_BASE_URL}/reports/sites`, { headers });
          setSiteSummary(siteRes.data.data);
          break;
        case 1:
          const matRes = await axios.get(`${API_BASE_URL}/reports/materials`, { headers });
          setMaterialUsage(matRes.data.data);
          break;
        case 2:
          const budgetRes = await axios.get(`${API_BASE_URL}/reports/budget`, { headers });
          setBudgetReport(budgetRes.data.data);
          break;
        case 3:
          await fetchTransferReport();
          break;
        case 4:
          const entryRes = await axios.get(`${API_BASE_URL}/reports/entry-summary?${queryString}`, { headers });
          setEntrySummary(entryRes.data.data);
          break;
        case 5:
          const deptRes = await axios.get(`${API_BASE_URL}/reports/department-spending?${queryString}`, { headers });
          setDepartmentSpending(deptRes.data.data);
          break;
        case 6:
          const supplierRes = await axios.get(`${API_BASE_URL}/reports/supplier-spending?${queryString}`, { headers });
          setSupplierSpending(supplierRes.data.data);
          break;
        case 7:
          const gstRes = await axios.get(`${API_BASE_URL}/reports/gst-summary?${queryString}`, { headers });
          setGSTSummary(gstRes.data.data);
          break;
        case 8:
          const paymentRes = await axios.get(`${API_BASE_URL}/reports/payment-source?${queryString}`, { headers });
          setPaymentSource(paymentRes.data.data);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransferReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (transferFilters.startDate) params.append('startDate', transferFilters.startDate);
      if (transferFilters.endDate) params.append('endDate', transferFilters.endDate);
      if (transferFilters.status && transferFilters.status !== 'all') params.append('status', transferFilters.status);
      if (transferFilters.fromSiteId) params.append('fromSiteId', transferFilters.fromSiteId);
      if (transferFilters.toSiteId) params.append('toSiteId', transferFilters.toSiteId);
      const response = await axios.get(`${API_BASE_URL}/reports/transfers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransferReport(response.data.data);
    } catch (err) {
      console.error('Error fetching transfer report:', err);
      throw err;
    }
  };

  // ✅ NEW: Supervisor-specific data fetch
  const fetchSupervisorData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const res = await axios.get(`${API_BASE_URL}/reports/entry-summary?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntrySummary(res.data.data);
    } catch (err) {
      console.error('Supervisor data fetch error:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.siteId) params.append('siteId', filters.siteId);

      const response = await axios.get(`${API_BASE_URL}/reports/entry-summary/pdf?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text'
      });

      const printWindow = window.open('', '_blank');
      printWindow.document.write(response.data);
      printWindow.document.close();
      printWindow.onload = () => { printWindow.print(); };
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Failed to generate PDF');
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('token');

      if (activeTab === 3) {
        const params = new URLSearchParams();
        if (transferFilters.startDate) params.append('startDate', transferFilters.startDate);
        if (transferFilters.endDate) params.append('endDate', transferFilters.endDate);
        if (transferFilters.status && transferFilters.status !== 'all') params.append('status', transferFilters.status);
        if (transferFilters.fromSiteId) params.append('fromSiteId', transferFilters.fromSiteId);
        if (transferFilters.toSiteId) params.append('toSiteId', transferFilters.toSiteId);
        const response = await axios.get(`${API_BASE_URL}/reports/transfers/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Transfer_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }

      // Client-side export for new tabs
      switch (activeTab) {
        case 4: {
          const wb = XLSX.utils.book_new();
          const rows = (entrySummary?.entries || []).map(e => ({
            Date: e.date ? new Date(e.date).toLocaleDateString() : '-',
            Site: e.siteName,
            Material: e.materialName,
            Quantity: e.quantity,
            Unit: e.unit,
            Supplier: e.supplierName,
            'Bill No': e.billNumber,
            Amount: e.amount
          }));
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Entry Summary');
          XLSX.writeFile(wb, `Entry_Summary_${new Date().toISOString().split('T')[0]}.xlsx`);
          return;
        }
        case 5: {
          const wb = XLSX.utils.book_new();
          const rows = (departmentSpending?.sites || []).flatMap(site =>
            (site.departments || []).flatMap(dept =>
              (dept.categories || []).map(cat => ({
                Site: site.siteName, Department: dept.departmentName,
                Category: cat.categoryName, Entries: cat.totalEntries, Amount: cat.totalAmount
              }))
            )
          );
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Dept Spending');
          XLSX.writeFile(wb, `Department_Spending_${new Date().toISOString().split('T')[0]}.xlsx`);
          return;
        }
        case 6: {
          const wb = XLSX.utils.book_new();
          const rows = (supplierSpending?.suppliers || []).map(s => ({
            Supplier: s.supplierName, Sites: s.sites?.map(x => x.siteName).join(', '),
            Bills: s.totalBills, Entries: s.totalEntries, Amount: s.totalAmount,
            'Last Purchase': s.lastPurchaseDate ? new Date(s.lastPurchaseDate).toLocaleDateString() : '-'
          }));
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Supplier Spending');
          XLSX.writeFile(wb, `Supplier_Spending_${new Date().toISOString().split('T')[0]}.xlsx`);
          return;
        }
        case 7: {
          const wb = XLSX.utils.book_new();
          const rows = (gstSummary?.gstBySlabs || []).map(s => ({
            'GST Slab': s.slab, Entries: s.totalEntries,
            'Base Amount': s.totalBaseAmount, 'GST Amount': s.totalGSTAmount, 'Total Amount': s.totalAmount
          }));
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'GST Summary');
          XLSX.writeFile(wb, `GST_Summary_${new Date().toISOString().split('T')[0]}.xlsx`);
          return;
        }
        case 8: {
          const wb = XLSX.utils.book_new();
          const rows = (paymentSource?.sites || []).map(s => ({
            Site: s.siteName, 'Company Entries': s.company?.totalEntries,
            'Company Amount': s.company?.totalAmount, 'Supervisor Entries': s.supervisor?.totalEntries,
            'Supervisor Amount': s.supervisor?.totalAmount, Total: s.totalAmount
          }));
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Payment Source');
          XLSX.writeFile(wb, `Payment_Source_${new Date().toISOString().split('T')[0]}.xlsx`);
          return;
        }
        default: break;
      }

      let reportType = '';
      let data = null;
      switch (activeTab) {
        case 0: reportType = 'Site Summary'; data = siteSummary; break;
        case 1: reportType = 'Material Usage'; data = materialUsage; break;
        case 2: reportType = 'Budget Report'; data = budgetReport; break;
        default: return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/reports/export/excel`,
        { reportType, data, filename: `${reportType}-${new Date().toISOString().split('T')[0]}` },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export report');
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0
  }).format(amount || 0);

  const getBudgetStatusColor = (status) => {
    switch (status) {
      case 'over_budget': return 'error';
      case 'near_limit': return 'warning';
      case 'under_budget': return 'success';
      default: return 'default';
    }
  };

  const getBudgetStatusLabel = (status) => {
    switch (status) {
      case 'over_budget': return '❌ Over Budget';
      case 'near_limit': return '⚠️ Near Limit';
      case 'under_budget': return '✅ Under Budget';
      default: return 'No Budget';
    }
  };

  const getTransferStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  // Common filter bar
  const FilterBar = ({ showSiteFilter = true }) => (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField fullWidth size="small" type="date" label="Start Date"
            value={filters.startDate}
            onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth size="small" type="date" label="End Date"
            value={filters.endDate}
            onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
        </Grid>
        {showSiteFilter && !isSupervisor && (
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" select label="Site"
              value={filters.siteId}
              onChange={(e) => setFilters(prev => ({ ...prev, siteId: e.target.value }))}>
              <MenuItem value="">All Sites</MenuItem>
              {sites.map(site => (
                <MenuItem key={site.id} value={site.id}>{site.siteName}</MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} sm={3}>
          {/* ✅ FIXED: Supervisor uses fetchSupervisorData, others use fetchReportData */}
          <Button variant="contained" onClick={isSupervisor ? fetchSupervisorData : fetchReportData} fullWidth>
            Apply Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  // ===== SUPERVISOR VIEW =====
  if (isSupervisor) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4">Reports & Analytics</Typography>
            <Typography variant="body2" color="text.secondary">Your site entry summary</Typography>
          </Box>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={handleExportPDF} color="error">
            Download PDF
          </Button>
        </Box>

        <FilterBar />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : entrySummary ? (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card><CardContent>
                  <Typography variant="body2" color="text.secondary">Total Entries</Typography>
                  <Typography variant="h5" fontWeight="bold">{entrySummary.summary?.totalEntries || 0}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card><CardContent>
                  <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {formatCurrency(entrySummary.summary?.totalAmount)}
                  </Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card><CardContent>
                  <Typography variant="body2" color="text.secondary">Sites</Typography>
                  <Typography variant="h5" fontWeight="bold">{entrySummary.summary?.totalSites || 0}</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>

            {entrySummary.bySite?.map((site, idx) => (
              <Accordion key={idx} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Typography fontWeight={600}>{site.siteName}</Typography>
                    <Typography color="primary" fontWeight={600}>{formatCurrency(site.totalAmount)}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Material</TableCell>
                          <TableCell>Qty</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>Bill No.</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {site.entries?.map((entry, i) => (
                          <TableRow key={i}>
                            <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                            <TableCell>{entry.materialName}</TableCell>
                            <TableCell>{entry.quantity}</TableCell>
                            <TableCell>{entry.unit}</TableCell>
                            <TableCell>{entry.supplierName}</TableCell>
                            <TableCell>{entry.billNumber}</TableCell>
                            <TableCell align="right">{formatCurrency(entry.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  }

  // ===== ADMIN/MANAGER VIEW =====
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Reports & Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Comprehensive reports and data export</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeTab === 4 && (
            <>
              <Button variant="contained" color="error" startIcon={<PdfIcon />} onClick={handleExportPDF}>
                Download PDF
              </Button>
              <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={handleExportExcel}>
                Export Excel
              </Button>
            </>
          )}
          {[0, 1, 2, 3, 5, 6, 7, 8].includes(activeTab) && (
            <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={handleExportExcel} disabled={loading}>
              Export Excel
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Site Summary" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Material Usage" icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label="Budget Analysis" icon={<MoneyIcon />} iconPosition="start" />
          <Tab label="Transfers" icon={<TransferIcon />} iconPosition="start" />
          <Tab label="Entry Summary" icon={<EntryIcon />} iconPosition="start" />
          <Tab label="Dept. Spending" icon={<DeptIcon />} iconPosition="start" />
          <Tab label="Supplier Spending" icon={<SupplierIcon />} iconPosition="start" />
          <Tab label="GST Summary" icon={<GSTIcon />} iconPosition="start" />
          <Tab label="Payment Source" icon={<PaymentIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {[4, 5, 6, 7, 8].includes(activeTab) && <FilterBar />}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Box>
          {/* Tab 0: Site Summary */}
          {activeTab === 0 && siteSummary && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Total Budget</Typography>
                    <Typography variant="h5">{formatCurrency(siteSummary.summary?.totalBudget)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Total Spending</Typography>
                    <Typography variant="h5" color="primary">{formatCurrency(siteSummary.summary?.totalSpending)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Sites Over Budget</Typography>
                    <Typography variant="h5" color="error">{siteSummary.summary?.overBudgetCount || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Sites Near Limit</Typography>
                    <Typography variant="h5" color="warning.main">{siteSummary.summary?.nearLimitCount || 0}</Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Site Name</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Budget</TableCell>
                      <TableCell align="right">Spending</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell align="center">Used %</TableCell>
                      <TableCell>Budget Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {siteSummary.sites?.map((site) => (
                      <TableRow key={site.id}>
                        <TableCell>{site.siteName}</TableCell>
                        <TableCell>{site.location}</TableCell>
                        <TableCell><Chip label={site.status} size="small" /></TableCell>
                        <TableCell align="right">{formatCurrency(site.projectedBudget)}</TableCell>
                        <TableCell align="right">{formatCurrency(site.actualSpending)}</TableCell>
                        <TableCell align="right">{formatCurrency(site.remaining)}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold"
                            color={site.percentageUsed >= 100 ? 'error' : site.percentageUsed >= 85 ? 'warning.main' : 'success.main'}>
                            {site.percentageUsed?.toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={getBudgetStatusLabel(site.budgetStatus)} color={getBudgetStatusColor(site.budgetStatus)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 1: Material Usage */}
          {activeTab === 1 && materialUsage && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Total Entries</Typography>
                    <Typography variant="h5">{materialUsage.summary?.totalEntries || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Total Amount</Typography>
                    <Typography variant="h5" color="primary">{formatCurrency(materialUsage.summary?.totalAmount)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Categories</Typography>
                    <Typography variant="h5">{materialUsage.summary?.byCategory?.length || 0}</Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Site</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Supplier</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materialUsage.entries?.slice(0, 20).map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell>{entry.date ? new Date(entry.date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>{entry.siteName}</TableCell>
                        <TableCell>{entry.category && entry.category !== 'Unknown' ? entry.category : entry.materialItemName || '-'}</TableCell>
                        <TableCell>
                          <Chip label={entry.entryType} color={entry.entryType === 'received' ? 'success' : 'warning'} size="small" />
                        </TableCell>
                        <TableCell align="right">{entry.quantity} {entry.unit}</TableCell>
                        <TableCell align="right">{formatCurrency(entry.amount)}</TableCell>
                        <TableCell>{entry.supplierName || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 2: Budget Analysis */}
          {activeTab === 2 && budgetReport && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Total Project Value</Typography>
                    <Typography variant="h5">{formatCurrency(budgetReport.summary?.totalProjectValue)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Total Budget</Typography>
                    <Typography variant="h5" color="info.main">{formatCurrency(budgetReport.summary?.totalBudget)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Total Spending</Typography>
                    <Typography variant="h5" color="primary">{formatCurrency(budgetReport.summary?.totalSpending)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="body2">Overall Used</Typography>
                    <Typography variant="h5"
                      color={budgetReport.summary?.overallPercentageUsed >= 100 ? 'error' : budgetReport.summary?.overallPercentageUsed >= 85 ? 'warning.main' : 'success.main'}>
                      {budgetReport.summary?.overallPercentageUsed?.toFixed(1)}%
                    </Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>
              {budgetReport.sites?.map((site) => (
                <Paper key={site.siteId} sx={{ mb: 3, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{site.siteName}</Typography>
                    <Chip label={getBudgetStatusLabel(site.budgetStatus)} color={getBudgetStatusColor(site.budgetStatus)} />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">Budget</Typography>
                      <Typography variant="body1" fontWeight="bold">{formatCurrency(site.projectedBudget)}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">Spending</Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary">{formatCurrency(site.actualSpending)}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">Remaining</Typography>
                      <Typography variant="body1" fontWeight="bold">{formatCurrency(site.remaining)}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">Used</Typography>
                      <Typography variant="body1" fontWeight="bold" color="success.main">{site.percentageUsed?.toFixed(1)}%</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          )}

          {/* Tab 3: Transfers */}
          {activeTab === 3 && (
            <Box>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Filters</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={2.4}>
                    <TextField fullWidth type="date" label="Start Date" value={transferFilters.startDate}
                      onChange={(e) => setTransferFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <TextField fullWidth type="date" label="End Date" value={transferFilters.endDate}
                      onChange={(e) => setTransferFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <TextField fullWidth select label="Status" value={transferFilters.status}
                      onChange={(e) => setTransferFilters(prev => ({ ...prev, status: e.target.value }))} size="small">
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <TextField fullWidth select label="From Site" value={transferFilters.fromSiteId}
                      onChange={(e) => setTransferFilters(prev => ({ ...prev, fromSiteId: e.target.value }))} size="small">
                      <MenuItem value="">All Sites</MenuItem>
                      {sites.map(site => <MenuItem key={site.id} value={site.id}>{site.siteName}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={2.4}>
                    <TextField fullWidth select label="To Site" value={transferFilters.toSiteId}
                      onChange={(e) => setTransferFilters(prev => ({ ...prev, toSiteId: e.target.value }))} size="small">
                      <MenuItem value="">All Sites</MenuItem>
                      {sites.map(site => <MenuItem key={site.id} value={site.id}>{site.siteName}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="contained" onClick={fetchTransferReport} size="small">Apply</Button>
                      <Button variant="outlined" onClick={() => { setTransferFilters({ startDate: '', endDate: '', status: 'all', fromSiteId: '', toSiteId: '' }); setTimeout(fetchTransferReport, 100); }} size="small">Reset</Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
              {transferReport && (
                <>
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    {[
                      { label: '📦 Total', value: transferReport.summary?.total, color: 'default' },
                      { label: '✅ Completed', value: transferReport.summary?.completed, color: '#E8F5E9' },
                      { label: '⏳ Pending', value: transferReport.summary?.pending, color: '#FFF3E0' },
                      { label: '❌ Rejected', value: transferReport.summary?.rejected, color: '#FFEBEE' },
                      { label: '🚫 Cancelled', value: transferReport.summary?.cancelled, color: '#F5F5F5' }
                    ].map((item, i) => (
                      <Grid item xs={6} sm={2.4} key={i}>
                        <Card sx={{ bgcolor: item.color !== 'default' ? item.color : 'white' }}>
                          <CardContent>
                            <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                            <Typography variant="h5">{item.value || 0}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Transfer #</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Material</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell>From Site</TableCell>
                          <TableCell>To Site</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Requested By</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transferReport.transfers?.length === 0 ? (
                          <TableRow><TableCell colSpan={9} align="center">No transfers found</TableCell></TableRow>
                        ) : (
                          transferReport.transfers?.map((transfer) => (
                            <TableRow key={transfer.id}>
                              <TableCell><Typography variant="body2" fontWeight="bold">{transfer.transferNumber}</Typography></TableCell>
                              <TableCell>{new Date(transfer.date).toLocaleDateString()}</TableCell>
                              <TableCell>{transfer.materialName}</TableCell>
                              <TableCell align="right">{transfer.quantity} {transfer.unit}</TableCell>
                              <TableCell>{transfer.fromSite?.name}</TableCell>
                              <TableCell>{transfer.toSite?.name}</TableCell>
                              <TableCell><Chip label={transfer.status?.toUpperCase()} color={getTransferStatusColor(transfer.status)} size="small" /></TableCell>
                              <TableCell>{transfer.requestedBy?.name}</TableCell>
                              <TableCell>{transfer.reason || '-'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}

          {/* Tab 4: Entry Summary */}
          {activeTab === 4 && (
            <Box>
              {entrySummary ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                      <Card><CardContent>
                        <Typography variant="body2" color="text.secondary">Total Entries</Typography>
                        <Typography variant="h5" fontWeight="bold">{entrySummary.summary?.totalEntries || 0}</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card><CardContent>
                        <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary">{formatCurrency(entrySummary.summary?.totalAmount)}</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card><CardContent>
                        <Typography variant="body2" color="text.secondary">Sites</Typography>
                        <Typography variant="h5" fontWeight="bold">{entrySummary.summary?.totalSites || 0}</Typography>
                      </CardContent></Card>
                    </Grid>
                  </Grid>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Site</TableCell>
                          <TableCell>Material</TableCell>
                          <TableCell>Qty</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>Bill No.</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entrySummary.entries?.length === 0 ? (
                          <TableRow><TableCell colSpan={8} align="center">No entries found</TableCell></TableRow>
                        ) : (
                          entrySummary.entries?.map((entry, i) => (
                            <TableRow key={i}>
                              <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                              <TableCell>{entry.siteName}</TableCell>
                              <TableCell>{entry.materialName}</TableCell>
                              <TableCell>{entry.quantity}</TableCell>
                              <TableCell>{entry.unit}</TableCell>
                              <TableCell>{entry.supplierName}</TableCell>
                              <TableCell>{entry.billNumber}</TableCell>
                              <TableCell align="right">{formatCurrency(entry.amount)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info">Select a date range and click Apply Filters to view entry summary.</Alert>
              )}
            </Box>
          )}

          {/* Tab 5: Department Spending */}
          {activeTab === 5 && (
            <Box>
              {departmentSpending ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={4}>
                      <Card sx={{ borderTop: '3px solid #64A8ED' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Civil Total</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#64A8ED">
                          {formatCurrency(departmentSpending.summary?.byDepartment?.Civil)}
                        </Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card sx={{ borderTop: '3px solid #C51668' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Electrical Total</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#C51668">
                          {formatCurrency(departmentSpending.summary?.byDepartment?.Electrical)}
                        </Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card sx={{ borderTop: '3px solid #4CAF50' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Grand Total</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#4CAF50">
                          {formatCurrency(departmentSpending.summary?.totalAmount)}
                        </Typography>
                      </CardContent></Card>
                    </Grid>
                  </Grid>
                  {departmentSpending.sites?.map((site, idx) => (
                    <Accordion key={idx} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                          <Typography fontWeight={600}>{site.siteName}</Typography>
                          <Typography color="primary" fontWeight={600}>{formatCurrency(site.totalAmount)}</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {site.departments?.map((dept, di) => (
                          <Box key={di} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography fontWeight={600} color={dept.departmentName === 'Civil' ? '#64A8ED' : '#C51668'}>
                                {dept.departmentName}
                              </Typography>
                              <Typography fontWeight={600}>{formatCurrency(dept.totalAmount)}</Typography>
                            </Box>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Expense Category</TableCell>
                                  <TableCell align="right">Entries</TableCell>
                                  <TableCell align="right">Amount</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {dept.categories?.map((cat, ci) => (
                                  <TableRow key={ci}>
                                    <TableCell>{cat.categoryName}</TableCell>
                                    <TableCell align="right">{cat.totalEntries}</TableCell>
                                    <TableCell align="right">{formatCurrency(cat.totalAmount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              ) : (
                <Alert severity="info">Apply filters to view department-wise spending.</Alert>
              )}
            </Box>
          )}

          {/* Tab 6: Supplier Spending */}
          {activeTab === 6 && (
            <Box>
              {supplierSpending ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={4}>
                      <Card><CardContent>
                        <Typography variant="body2" color="text.secondary">Total Suppliers</Typography>
                        <Typography variant="h5" fontWeight="bold">{supplierSpending.summary?.totalSuppliers || 0}</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card><CardContent>
                        <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary">{formatCurrency(supplierSpending.summary?.totalAmount)}</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card><CardContent>
                        <Typography variant="body2" color="text.secondary">Total Bills</Typography>
                        <Typography variant="h5" fontWeight="bold">{supplierSpending.summary?.totalBills || 0}</Typography>
                      </CardContent></Card>
                    </Grid>
                  </Grid>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Supplier Name</TableCell>
                          <TableCell>Sites</TableCell>
                          <TableCell align="right">Total Bills</TableCell>
                          <TableCell align="right">Total Entries</TableCell>
                          <TableCell align="right">Total Amount</TableCell>
                          <TableCell>Last Purchase</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {supplierSpending.suppliers?.map((supplier, i) => (
                          <TableRow key={i}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell><Typography fontWeight={600}>{supplier.supplierName}</Typography></TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {supplier.sites?.map(s => s.siteName).join(', ')}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{supplier.totalBills}</TableCell>
                            <TableCell align="right">{supplier.totalEntries}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={600} color="primary">{formatCurrency(supplier.totalAmount)}</Typography>
                            </TableCell>
                            <TableCell>
                              {supplier.lastPurchaseDate ? new Date(supplier.lastPurchaseDate).toLocaleDateString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info">Apply filters to view supplier-wise spending.</Alert>
              )}
            </Box>
          )}

          {/* Tab 7: GST Summary */}
          {activeTab === 7 && (
            <Box>
              {gstSummary ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                      <Card sx={{ borderTop: '3px solid #64A8ED' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">GST Bills</Typography>
                        <Typography variant="h5" fontWeight="bold">{gstSummary.summary?.gst?.totalEntries || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">Base: {formatCurrency(gstSummary.summary?.gst?.totalBaseAmount)}</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card sx={{ borderTop: '3px solid #FB9637' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Total GST Paid</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#FB9637">{formatCurrency(gstSummary.summary?.gst?.totalGSTAmount)}</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card sx={{ borderTop: '3px solid #C51668' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Non-GST Bills</Typography>
                        <Typography variant="h5" fontWeight="bold">{gstSummary.summary?.nonGST?.totalEntries || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">{formatCurrency(gstSummary.summary?.nonGST?.totalAmount)}</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card sx={{ borderTop: '3px solid #4CAF50' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Overall Total</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#4CAF50">{formatCurrency(gstSummary.summary?.overall?.totalAmount)}</Typography>
                      </CardContent></Card>
                    </Grid>
                  </Grid>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>GST Slab Breakdown</Typography>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>GST Slab</TableCell>
                          <TableCell align="right">Entries</TableCell>
                          <TableCell align="right">Base Amount</TableCell>
                          <TableCell align="right">GST Amount</TableCell>
                          <TableCell align="right">Total Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {gstSummary.gstBySlabs?.map((slab, i) => (
                          <TableRow key={i}>
                            <TableCell><Chip label={slab.slab} color="primary" size="small" /></TableCell>
                            <TableCell align="right">{slab.totalEntries}</TableCell>
                            <TableCell align="right">{formatCurrency(slab.totalBaseAmount)}</TableCell>
                            <TableCell align="right" sx={{ color: '#FB9637', fontWeight: 600 }}>{formatCurrency(slab.totalGSTAmount)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(slab.totalAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info">Apply filters to view GST summary.</Alert>
              )}
            </Box>
          )}

          {/* Tab 8: Payment Source */}
          {activeTab === 8 && (
            <Box>
              {paymentSource ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={4}>
                      <Card sx={{ borderTop: '3px solid #64A8ED' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Company Payments</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#64A8ED">{formatCurrency(paymentSource.summary?.company?.totalAmount)}</Typography>
                        <Typography variant="caption" color="text.secondary">{paymentSource.summary?.company?.totalEntries} entries</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card sx={{ borderTop: '3px solid #FB9637' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Supervisor Payments</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#FB9637">{formatCurrency(paymentSource.summary?.supervisor?.totalAmount)}</Typography>
                        <Typography variant="caption" color="text.secondary">{paymentSource.summary?.supervisor?.totalEntries} entries (reimbursable)</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card sx={{ borderTop: '3px solid #4CAF50' }}><CardContent>
                        <Typography variant="body2" color="text.secondary">Grand Total</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#4CAF50">{formatCurrency(paymentSource.summary?.total)}</Typography>
                      </CardContent></Card>
                    </Grid>
                  </Grid>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Site</TableCell>
                          <TableCell align="right">Company Entries</TableCell>
                          <TableCell align="right">Company Amount</TableCell>
                          <TableCell align="right">Supervisor Entries</TableCell>
                          <TableCell align="right">Supervisor Amount (Reimbursable)</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentSource.sites?.map((site, i) => (
                          <TableRow key={i}>
                            <TableCell><Typography fontWeight={600}>{site.siteName}</Typography></TableCell>
                            <TableCell align="right">{site.company?.totalEntries || 0}</TableCell>
                            <TableCell align="right" sx={{ color: '#64A8ED' }}>{formatCurrency(site.company?.totalAmount)}</TableCell>
                            <TableCell align="right">{site.supervisor?.totalEntries || 0}</TableCell>
                            <TableCell align="right" sx={{ color: '#FB9637', fontWeight: 600 }}>{formatCurrency(site.supervisor?.totalAmount)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(site.totalAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info">Apply filters to view payment source report.</Alert>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReportsPage;